import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getTemplateFilters } from '@/services';

export interface PosterDataMapConfig {
  /** Custom name for posters */
  name?: string;
  /** Custom address for posters */
  address?: string;
  /** Custom phone number for posters */
  phone?: string;
  /** Custom logo URL for posters */
  logo?: string;
  /** Custom user name for posters */
  user_name?: string;
}

interface PosterState {
  /** Available poster store types */
  storeTypes: string[];
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: string | null;
  /** Last fetched timestamp */
  lastFetchedAt: number | null;
  /** Custom poster data map configurations per identifier (storeId or 'buyer') */
  posterDataMapByStoreId: Record<string, PosterDataMapConfig>;
}

const initialState: PosterState = {
  storeTypes: [],
  isLoading: false,
  error: null,
  lastFetchedAt: null,
  posterDataMapByStoreId: {},
};

/**
 * Fetch poster store types from the API
 * This fetches all available poster store types and stores them in Redux
 */
export const fetchPosterStoreTypes = createAsyncThunk(
  'poster/fetchStoreTypes',
  async (_, { rejectWithValue }) => {
    try {
      const { storeTypes } = await getTemplateFilters();
      return storeTypes;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch poster store types'
      );
    }
  }
);

const posterSlice = createSlice({
  name: 'poster',
  initialState,
  reducers: {
    /** Clear all poster state (useful for logout/reset) */
    clearPosterState: (state) => {
      state.storeTypes = [];
      state.isLoading = false;
      state.error = null;
      state.lastFetchedAt = null;
      state.posterDataMapByStoreId = {};
    },
    /** Clear poster data map configuration for a specific identifier */
    clearPosterDataMap: (state, action: PayloadAction<string>) => {
      delete state.posterDataMapByStoreId[action.payload];
    },
    /** Update poster data map with store ID - stores config per identifier */
    updatePosterDataMap: (state, action: PayloadAction<{ dataMap: PosterDataMapConfig; storeId: string }>) => {
      state.posterDataMapByStoreId[action.payload.storeId] = action.payload.dataMap;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPosterStoreTypes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPosterStoreTypes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.storeTypes = action.payload;
        state.error = null;
        state.lastFetchedAt = Date.now();
      })
      .addCase(fetchPosterStoreTypes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string || 'Failed to fetch poster store types';
      });
  },
});

export default posterSlice.reducer;

// Selectors
export const selectPosterStoreTypes = (state: { poster: PosterState }) => state.poster.storeTypes;
export const selectIsLoadingPosterFilters = (state: { poster: PosterState }) => state.poster.isLoading;
export const selectPosterFiltersError = (state: { poster: PosterState }) => state.poster.error;
export const selectPosterFiltersLastFetched = (state: { poster: PosterState }) => state.poster.lastFetchedAt;
export const selectHasPosterStoreTypes = (state: { poster: PosterState }) => 
  state.poster.storeTypes.length > 0;
/** Get poster data map for a specific identifier (storeId or 'buyer') */
export const selectPosterDataMap = (storeId: string) => (state: { poster: PosterState }) => 
  state.poster.posterDataMapByStoreId[storeId];
/** Get all poster data maps */
export const selectAllPosterDataMaps = (state: { poster: PosterState }) => 
  state.poster.posterDataMapByStoreId;

// Actions
export const { 
  clearPosterState, 
  clearPosterDataMap,
  updatePosterDataMap,
} = posterSlice.actions;

